import type { SpApiInventoryRow, SpApiSalesAsinRow, SpApiTrafficRow } from '@/api/client';

function num(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Last complete vendor retail day (Amazon Vendor reports lag 2 days, IST). */
export function vendorLag2DateIso(now = new Date()): string {
  const istToday = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return addDaysIso(istToday, -2);
}

export function clipRangeToVendorLag2(
  startDate: string,
  endDate: string,
  dataThrough = vendorLag2DateIso()
): { startDate: string; endDate: string; dataThrough: string; clipped: boolean } {
  const clippedEnd = endDate > dataThrough ? dataThrough : endDate;
  return {
    startDate,
    endDate: clippedEnd,
    dataThrough,
    clipped: endDate > dataThrough,
  };
}

export type VendorSalesTotals = {
  orderedRevenue: number;
  orderedUnits: number;
  shippedUnits: number;
  customerReturns: number;
  asinCount: number;
  currency: string | null;
};

export function summarizeVendorSales(rows: SpApiSalesAsinRow[]): VendorSalesTotals {
  const asins = new Set<string>();
  let orderedRevenue = 0;
  let orderedUnits = 0;
  let shippedUnits = 0;
  let customerReturns = 0;
  let currency: string | null = null;
  for (const row of rows) {
    const asin = (row.asin || '').trim();
    if (asin) asins.add(asin);
    orderedRevenue += num(row.orderedRevenue);
    orderedUnits += num(row.orderedUnits);
    shippedUnits += num(row.shippedUnits);
    customerReturns += num(row.customerReturns);
    currency = row.currency || currency;
  }
  return {
    orderedRevenue,
    orderedUnits,
    shippedUnits,
    customerReturns,
    asinCount: asins.size,
    currency,
  };
}

export type VendorSalesAsinRollup = {
  asin: string;
  orderedUnits: number;
  orderedRevenue: number;
  shippedUnits: number;
  shippedRevenue: number;
  customerReturns: number;
  currency: string | null;
};

export function rollupSalesByAsin(rows: SpApiSalesAsinRow[]): VendorSalesAsinRollup[] {
  const map = new Map<string, VendorSalesAsinRollup>();
  for (const row of rows) {
    const asin = (row.asin || '').trim();
    if (!asin) continue;
    const cur = map.get(asin) ?? {
      asin,
      orderedUnits: 0,
      orderedRevenue: 0,
      shippedUnits: 0,
      shippedRevenue: 0,
      customerReturns: 0,
      currency: null,
    };
    cur.orderedUnits += num(row.orderedUnits);
    cur.orderedRevenue += num(row.orderedRevenue);
    cur.shippedUnits += num(row.shippedUnits);
    cur.shippedRevenue += num(row.shippedRevenue);
    cur.customerReturns += num(row.customerReturns);
    cur.currency = row.currency || cur.currency;
    map.set(asin, cur);
  }
  return [...map.values()].sort(
    (a, b) => b.orderedRevenue - a.orderedRevenue || a.asin.localeCompare(b.asin)
  );
}

export type VendorTrafficAsinRollup = { asin: string; glanceViews: number };

export function rollupTrafficByAsin(rows: SpApiTrafficRow[]): VendorTrafficAsinRollup[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    const asin = (row.asin || '').trim();
    if (!asin) continue;
    map.set(asin, (map.get(asin) ?? 0) + num(row.glanceViews));
  }
  return [...map.entries()]
    .map(([asin, glanceViews]) => ({ asin, glanceViews }))
    .sort((a, b) => b.glanceViews - a.glanceViews || a.asin.localeCompare(b.asin));
}

export function sumGlanceViews(rows: SpApiTrafficRow[]): number {
  return rows.reduce((sum, row) => sum + num(row.glanceViews), 0);
}

function rowDay(row: { startDate?: string | null; endDate?: string | null }): string {
  return (row.endDate || row.startDate || '').slice(0, 10);
}

export function latestInventoryRows(rows: SpApiInventoryRow[]): {
  asOf: string | null;
  rows: SpApiInventoryRow[];
} {
  let latest = '';
  for (const row of rows) {
    const day = rowDay(row);
    if (day && day > latest) latest = day;
  }
  if (!latest) return { asOf: null, rows: [] };
  const byAsin = new Map<string, SpApiInventoryRow>();
  for (const row of rows) {
    if (rowDay(row) !== latest) continue;
    const asin = (row.asin || '').trim();
    if (!asin) continue;
    byAsin.set(asin, row);
  }
  return {
    asOf: latest,
    rows: [...byAsin.values()].sort((a, b) => a.asin.localeCompare(b.asin)),
  };
}
